import { getTimeSinceLastActivity } from './userActivity';

// Store the logout function provided by AuthContext
let logoutCallback = null;
let refreshCallback = null;
let isLoggingOut = false;

/**
 * Initialize the API middleware with callbacks
 * @param {Function} logoutFn - Function to call when refresh fails
 * @param {Function} refreshFn - Function to refresh tokens
 */
export const initializeApiMiddleware = (logoutFn, refreshFn) => {
  logoutCallback = logoutFn;
  refreshCallback = refreshFn;
  isLoggingOut = false;
};

/**
 * Set the logging out state to prevent refresh attempts during logout
 * @param {boolean} loggingOut - Whether logout is in progress
 */
export const setLoggingOutState = (loggingOut) => {
  isLoggingOut = loggingOut;
};

/**
 * Check if token is close to expiring (within 1 minute)
 * @param {string} token - JWT token
 * @returns {boolean}
 */
const isTokenNearExpiry = (token) => {
  if (!token) {
    return true;
  }
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    const expiryTime = payload.exp;
    const timeUntilExpiry = expiryTime - now;
    // console.log('🔐 [TOKEN CHECK] Time until token expiry in minutes(s):', timeUntilExpiry / 60);
    
    const isNearExpiry = timeUntilExpiry < 60;
    
    
    // Return true if token expires within 1 minute
    return isNearExpiry;
  } catch (error) {
    console.error('🔐 [TOKEN CHECK] Error parsing token:', error);
    return true;
  }
};

/**
 * Check if token should be refreshed based on user activity
 * @returns {boolean}
 */
const shouldRefreshToken = () => {
  // Only refresh if user has been active recently (within 60 seconds)
  // This prevents background polling from keeping inactive sessions alive
  const timeSinceActivity = getTimeSinceLastActivity();
  const hasRecentActivity = timeSinceActivity < 60000; // 1 minute
  
  return hasRecentActivity;
};

/**
 * Middleware for handling API responses with proper error handling
 * @param {Response} response - The fetch API response
 * @param {string} token - Current access token
 * @returns {Promise<Response>} - The response or throws an error
 */
export const handleApiResponse = async (response, token = null) => {
  // If unauthorized (401), try to refresh token first
  if (response.status === 401) {
    
    // Don't attempt refresh if we're already logging out
    if (isLoggingOut) {
      console.error("Session expired. Logging out.");
    }
    
    // Check if we should attempt refresh based on user activity
    const shouldRefresh = shouldRefreshToken();
    if (!shouldRefresh) {
      
      isLoggingOut = true;
      if (logoutCallback) {
        logoutCallback();
        window.location.href = "/login";
      }
      throw new Error("Session expired due to inactivity. Please log in again.");
    }
    
    if (refreshCallback) {
      try {
        const newToken = await refreshCallback();
        
        if (newToken) {
          // Token refreshed successfully, but this request still failed
          // The caller should retry with the new token
          throw new Error("TOKEN_REFRESHED");
        }
      } catch (refreshError) {
        console.error("❌ [REFRESH] Token refresh failed:", refreshError);
      }
    }
    
    // If refresh failed or no refresh callback, log out
    isLoggingOut = true;
    if (logoutCallback) {
      logoutCallback();
      window.location.href = "/login";
    }
    
    throw new Error("Your session has expired. Please log in again.");
  }
  
  return response;
};

/**
 * Centralized API request function with automatic token refresh
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Fetch options
 * @param {string} token - The authentication token
 * @param {boolean} isUserInitiated - Whether this request was initiated by user action
 * @returns {Promise<any>} - The parsed response data
 */
export const apiRequest = async (url, options = {}, token = null, isUserInitiated = false) => {
  
  try {
    // Don't make requests if we're logging out
    if (isLoggingOut) {
      throw new Error("Request cancelled: logout in progress");
    }
    
    // Check if token needs refresh before making request
    if (token && isTokenNearExpiry(token) && refreshCallback && !isLoggingOut) {
      
      // Check if there's recent user activity to determine if we should refresh
      const shouldRefresh = shouldRefreshToken();
      
      if (shouldRefresh) {
        try {
          const newToken = await refreshCallback();
          // Get the refreshed token from localStorage since refreshCallback updates it there
          token = localStorage.getItem('token') || newToken;
        } catch (error) {
          console.error("❌ [PROACTIVE REFRESH] Failed:", error);
        }
      } 
    }
    
    // Set up headers with authentication
    const headers = {
      ...options.headers || {},
    };
    
    
    // Only set Content-Type to JSON if it's not already set and body is not FormData
    if (!headers["Content-Type"] && !(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }
    
    // If body is FormData, don't set Content-Type - let browser handle it automatically
    if (options.body instanceof FormData) {
      delete headers['Content-Type']; // Remove if already set
    }
    
    
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    let response = await fetch(url, {
      ...options,
      headers
    });
    
    // Process response through middleware
    try {
      await handleApiResponse(response, token);
    } catch (error) {
      // If token was refreshed, retry the request once
      if (error.message === "TOKEN_REFRESHED" && refreshCallback) {
        const newToken = localStorage.getItem('token');
        if (newToken) {
          headers["Authorization"] = `Bearer ${newToken}`;
          
          response = await fetch(url, {
            ...options,
            headers
          });
          
          await handleApiResponse(response, newToken);
        } else {
          throw error;
        }
      } else {
        throw error;
      }
    }
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    // Parse response based on content type
    const contentType = response.headers.get("Content-Type");
    if (contentType && contentType.includes("application/json")) {
      return await response.json();
    }
    
    return await response.text();
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

// Convenience function for user-initiated requests (explicit button clicks, form submissions)
export const userApiRequest = (url, options = {}, token = null) => {
  return apiRequest(url, options, token, true);
};

// Convenience function for background requests (default behavior)
export const backgroundApiRequest = (url, options = {}, token = null) => {
  return apiRequest(url, options, token, false);
};

export default apiRequest;
